import { useFrame, useLoader, useThree } from "@react-three/fiber";
import { useRef, useEffect } from "react";
import * as THREE from "three";
import vertexShader from "./shaders/vertex.glsl";
import fragmentShader from "./shaders/fragment.glsl";
import noiseFragmentShader from "./shaders/noise.glsl";

const PALETTES = {
    "Neon Flux": [
        [0.02, 0.2, 0.75], // blue
        [0.8, 0.05, 0.55], // magenta
        [0.95, 0.1, 0.15], // red
        [0.97, 0.48, 0.08], // orange
        [0.2, 0.65, 0.88], // teal/cyan
    ],
    Sunset: [
        [0.95, 0.25, 0.05], // deep orange
        [0.85, 0.08, 0.35], // crimson
        [1.0, 0.6, 0.0], // amber
        [0.55, 0.05, 0.5], // purple
        [1.0, 0.85, 0.2], // gold
    ],
    Aurora: [
        [0.0, 0.75, 0.45], // emerald green
        [0.05, 0.45, 0.95], // bright blue
        [0.55, 0.05, 0.85], // violet
        [0.0, 0.9, 0.7], // cyan-green
        [0.3, 0.0, 0.65], // deep purple
    ],
    "NASA Red": [
        [0.851, 0.161, 0.024], // NASA red #D92906
        [0.98, 0.42, 0.12], // warm orange
        [0.62, 0.03, 0.1], // deep crimson
        [0.99, 0.55, 0.42], // coral / salmon
        [0.4, 0.02, 0.14], // burgundy
    ],
    Smoke: [
        [0.22, 0.22, 0.22], // charcoal #383838
        [0.247, 0.247, 0.247], // dark grey #3F3F3F
        [0.302, 0.302, 0.302], // grey #4D4D4D
        [0.451, 0.451, 0.451], // mid grey #737373
        [0.58, 0.58, 0.58], // light grey #949494
    ],
};

// Per-palette background color + compositing mode.
// light: mix bg toward blob color (pale bg). dark: additive HDR blobs (near-black bg).
const PALETTE_META = {
    "Neon Flux": { bg: [0.005, 0.01, 0.055], light: false },
    Sunset: { bg: [0.005, 0.01, 0.055], light: false },
    Aurora: { bg: [0.005, 0.01, 0.055], light: false },
    "NASA Red": { bg: [0.96, 0.94, 0.9], light: true },
    Smoke: { bg: [0.9, 0.9, 0.9], light: true },
};

const MOUSE_MODE = {
    Off: 0,
    Repel: 1,
    Attract: 2,
    Swirl: 3,
    Drag: 4,
    Lens: 5,
    "Pin blob": 6,
    "Repel centers": 7,
    "Cursor blob": 8,
};

export default function Experience() {
    const quadRef = useRef();
    const gl = useThree((s) => s.gl);
    const easedMouse = useRef({ x: 0, y: 0 });
    const mouseActive = useRef(0);
    const pointerOver = useRef(false);
    const easedVel = useRef({ x: 0, y: 0 });
    const prevMouse = useRef({ x: 0, y: 0 });

    // Frozen background settings (Neon Flux palette, now locked in).
    const palette = "Neon Flux";
    const algo = "Algo2";
    const mouseMode = "Repel centers";
    const mouseStrength = 0.1;
    const mouseRadius = 1.0;
    const repelSoftness = 0.5;
    const noiseScaleX = 0.35;
    const noiseScaleY = 0.55;
    const warpStrength = 0.4;
    const warpSpeed = 0.12;
    const grainStrength = 0.5;
    const fluteWidth = 90; // 1440 / 90 = 16 flutes across (viewport-scaled)
    const fluteStrength = 140;
    const patternBrightness = 0.9;

    const noiseSceneRef = useRef(null);
    const noiseCameraRef = useRef(null);
    const noiseFBORef = useRef(null);
    const noiseUniformsRef = useRef({
        uTime: { value: 0 },
        uNoiseScaleX: { value: 1.4 },
        uNoiseScaleY: { value: 1 },
        uWarpSpeed: { value: 0.12 },
    });

    if (!noiseSceneRef.current) {
        const rt = new THREE.WebGLRenderTarget(256, 256, {
            format: THREE.RGBAFormat,
            magFilter: THREE.LinearFilter,
            minFilter: THREE.LinearFilter,
        });
        rt.texture.wrapS = THREE.MirroredRepeatWrapping;
        rt.texture.wrapT = THREE.MirroredRepeatWrapping;
        noiseFBORef.current = rt;

        const scene = new THREE.Scene();
        scene.add(
            new THREE.Mesh(
                new THREE.PlaneGeometry(2, 2),
                new THREE.ShaderMaterial({
                    vertexShader,
                    fragmentShader: noiseFragmentShader,
                    uniforms: noiseUniformsRef.current,
                }),
            ),
        );
        noiseSceneRef.current = scene;
        noiseCameraRef.current = new THREE.OrthographicCamera(
            -1,
            1,
            1,
            -1,
            0,
            1,
        );
    }

    const grainTexture = useLoader(
        THREE.TextureLoader,
        "/film_grain_contrasted.jpg",
    );

    useEffect(() => {
        grainTexture.wrapS = THREE.RepeatWrapping;
        grainTexture.wrapT = THREE.RepeatWrapping;
        grainTexture.needsUpdate = true;
    }, [grainTexture]);

    useEffect(() => {
        const el = gl.domElement;
        const onEnter = () => (pointerOver.current = true);
        const onLeave = () => (pointerOver.current = false);
        el.addEventListener("pointerenter", onEnter);
        el.addEventListener("pointermove", onEnter);
        el.addEventListener("pointerleave", onLeave);
        return () => {
            el.removeEventListener("pointerenter", onEnter);
            el.removeEventListener("pointermove", onEnter);
            el.removeEventListener("pointerleave", onLeave);
        };
    }, [gl]);

    const uniformsRef = useRef({
        uResolution: {
            value: new THREE.Vector2(window.innerWidth, window.innerHeight),
        },
        uPixelRatio: {
            value: window.devicePixelRatio,
        },
        uTime: {
            value: 0,
        },
        uWarpStrength: { value: 0.3 },
        uNoiseMap: { value: noiseFBORef.current.texture },
        uGrainTexture: { value: grainTexture },
        uGrainTextureSize: {
            value: new THREE.Vector2(1920, 1260),
        },
        uGrainStrength: { value: 0.05 },
        uFluteWidth: { value: 50.0 },
        uFluteStrength: { value: 70.0 },
        uToneMapExposure: { value: 0.1 },
        uC1: { value: new THREE.Vector3(...PALETTES["Neon Flux"][0]) },
        uC2: { value: new THREE.Vector3(...PALETTES["Neon Flux"][1]) },
        uC3: { value: new THREE.Vector3(...PALETTES["Neon Flux"][2]) },
        uC4: { value: new THREE.Vector3(...PALETTES["Neon Flux"][3]) },
        uC5: { value: new THREE.Vector3(...PALETTES["Neon Flux"][4]) },
        uBackground: { value: new THREE.Vector3(0.005, 0.01, 0.055) },
        uLightMode: { value: 0 },
        uAlgo: { value: 1 },
        uMouse: { value: new THREE.Vector2(0, 0) },
        uMouseMode: { value: 1 },
        uMouseStrength: { value: 0.35 },
        uMouseRadius: { value: 0.3 },
        uMouseActive: { value: 0 },
        uMouseVelocity: { value: new THREE.Vector2(0, 0) },
        uRepelSoftness: { value: 0.15 },
    });

    useFrame((state, delta) => {
        noiseUniformsRef.current.uTime.value += delta;
        noiseUniformsRef.current.uNoiseScaleX.value = noiseScaleX;
        noiseUniformsRef.current.uNoiseScaleY.value = noiseScaleY;
        noiseUniformsRef.current.uWarpSpeed.value = warpSpeed;
        gl.setRenderTarget(noiseFBORef.current);
        gl.render(noiseSceneRef.current, noiseCameraRef.current);
        gl.setRenderTarget(null);

        // NOTE: r3f constructs the ShaderMaterial from the `uniforms` prop, which
        // detaches it from `uniformsRef.current`. Write to the live material's
        // uniforms so scalar updates (time, flute, brightness, lightMode…) apply.
        const mat = quadRef.current?.material;
        if (!mat) return;
        const u = mat.uniforms;

        u.uResolution.value.set(state.size.width, state.size.height);
        u.uTime.value += delta;
        u.uWarpStrength.value = warpStrength;
        if (grainTexture.image) {
            u.uGrainTextureSize.value.set(
                grainTexture.image.width,
                grainTexture.image.height,
            );
        }
        u.uGrainStrength.value = grainStrength;
        u.uFluteWidth.value = fluteWidth;
        u.uFluteStrength.value = fluteStrength;
        u.uToneMapExposure.value = patternBrightness;
        u.uPixelRatio.value = window.devicePixelRatio;
        const pal = PALETTES[palette];
        u.uC1.value.set(...pal[0]);
        u.uC2.value.set(...pal[1]);
        u.uC3.value.set(...pal[2]);
        u.uC4.value.set(...pal[3]);
        u.uC5.value.set(...pal[4]);
        const meta = PALETTE_META[palette];
        u.uBackground.value.set(...meta.bg);
        u.uLightMode.value = meta.light ? 1 : 0;
        u.uAlgo.value = algo === "Algo1" ? 0 : 1;

        const k = 1 - Math.exp(-delta * 8);
        // Map the cursor into the same viewport-normalised space the shader now
        // samples in (mappedCoords / (1000 * uResolution.x / 1440)). This keeps
        // the mouse interaction aligned with the scaled pattern on any screen.
        const REF_WIDTH = 1440;
        const targetX = (state.pointer.x * REF_WIDTH) / 2000;
        const targetY =
            (state.pointer.y * REF_WIDTH * state.size.height) /
            (2000 * state.size.width);
        const prevX = easedMouse.current.x;
        const prevY = easedMouse.current.y;
        easedMouse.current.x += (targetX - prevX) * k;
        easedMouse.current.y += (targetY - prevY) * k;
        const instVelX = easedMouse.current.x - prevMouse.current.x;
        const instVelY = easedMouse.current.y - prevMouse.current.y;
        prevMouse.current.x = easedMouse.current.x;
        prevMouse.current.y = easedMouse.current.y;
        easedVel.current.x += (instVelX - easedVel.current.x) * k;
        easedVel.current.y += (instVelY - easedVel.current.y) * k;
        const activeTarget =
            mouseMode !== "Off" && pointerOver.current ? 1 : 0;
        mouseActive.current += (activeTarget - mouseActive.current) * k;
        u.uMouse.value.set(easedMouse.current.x, easedMouse.current.y);
        u.uMouseMode.value = MOUSE_MODE[mouseMode];
        u.uMouseStrength.value = mouseStrength;
        u.uMouseRadius.value = mouseRadius;
        u.uMouseActive.value = mouseActive.current;
        u.uMouseVelocity.value.set(easedVel.current.x, easedVel.current.y);
        u.uRepelSoftness.value = repelSoftness;
    });

    return (
        <>
            <mesh ref={quadRef}>
                <planeGeometry args={[2, 2, 1, 1]} />
                <shaderMaterial
                    vertexShader={vertexShader}
                    fragmentShader={fragmentShader}
                    uniforms={uniformsRef.current}
                />
            </mesh>
        </>
    );
}
